import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Check, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAnimationSettings } from "@/context/AnimationContext";
import { useThemeInfo } from "@/context/ThemeContext";
import { useAppSelector } from "@/store/hooks";
import { selectUser } from "@/store/slices/authSlice";

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme } = useTheme();
  const { availableThemes, saveThemePreference, isThemeSyncing } = useThemeInfo();
  const { animationsEnabled, setAnimationsEnabled } = useAnimationSettings();
  const currentUser = useAppSelector(selectUser);
  const [mounted, setMounted] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Effect to ensure component is mounted before rendering potentially client-side dependent UI
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Track selected carousel item
  useEffect(() => {
    if (!carouselApi) return;
    
    const onChange = (api: CarouselApi) => {
      if (api) {
        console.log("Selected index changed:", api.selectedScrollSnap());
        setSelectedIndex(api.selectedScrollSnap());
      }
    };
    
    carouselApi.on("select", onChange);
    return () => {
      carouselApi.off("select", onChange);
    };
  }, [carouselApi]);

  // Set initial carousel index to match current theme
  useEffect(() => {
    if (!carouselApi || !theme || !mounted) return;
    
    const themeIndex = availableThemes.findIndex(t => t.value === theme);
    if (themeIndex !== -1) {
      carouselApi.scrollTo(themeIndex);
    }
  }, [carouselApi, theme, mounted, availableThemes]);

  const handleAnimationToggle = (checked: boolean) => {
    setAnimationsEnabled(checked);
  };

  // Avoid hydration mismatch
  if (!mounted) {
    return null;
  }
  
  const currentTheme = theme || 'system';

  // Filter out 'system' theme if needed
  const displayThemes = availableThemes;
  
  // Determine currently selected theme in the carousel
  const selectedTheme = displayThemes[selectedIndex]?.value;
  
  // Function to apply the selected theme
  const applySelectedTheme = async () => {
    if (!selectedTheme) return;
    
    if (currentUser) {
      // If user is logged in, save the theme to their profile
      await saveThemePreference(selectedTheme);
    } else {
      // If no user is logged in, just set the theme locally
      // This is handled in saveThemePreference when there's no currentUser
      await saveThemePreference(selectedTheme);
    }
  };

  return (
    <div className={`p-4 space-y-6 ${className}`}>
       <h3 className="text-lg font-medium text-center mb-4">Select Theme</h3>
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full max-w-xs mx-auto"
        setApi={setCarouselApi}
      >
        <CarouselContent>
          {displayThemes.map((t) => (
            <CarouselItem key={t.value} className="basis-full">
              <Card 
                className={`overflow-hidden h-full ${currentTheme === t.value ? 'border-primary border-2' : ''}`}
                style={
                  (t.colors && t.colors.chart && t.colors.chart.length > 0) ? {
                    backgroundImage: `linear-gradient(135deg, ${t.colors.chart.map((color, index, arr) => 
                      `${color} ${index * (100 / arr.length)}%, ${color} ${(index + 1) * (100 / arr.length)}%`
                    ).join(', ')})`,
                  } : {}
                }
              >
                <CardContent 
                  className="p-4 flex flex-col justify-center items-center m-0 h-full"
                >
                  {(t.colors && t.colors.background && t.colors.foreground) ? (
                    <div 
                      className="p-4 rounded border-2 shadow-md text-center min-w-[100px]"
                      style={{
                        backgroundColor: t.colors.background,
                        borderColor: t.colors.border,
                        color: t.colors.foreground,
                      }}
                    >
                      {t.name}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center flex-grow">
                        <Palette className="h-8 w-8 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground mt-1">{t.name}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
      
      {/* Set Theme Button */}
      <div className="flex justify-center mt-4">
        <Button 
          variant="outline" 
          size="lg"
          onClick={applySelectedTheme}
          disabled={currentTheme === selectedTheme || isThemeSyncing}
          className="w-full max-w-xs"
        >
          {isThemeSyncing ? (
            "Saving..."
          ) : currentTheme === selectedTheme ? (
            <><Check className="h-4 w-4 mr-2"/> Current Theme</>
          ) : (
            `Set ${displayThemes.find(t => t.value === selectedTheme)?.name || selectedTheme} Theme`
          )}
        </Button>
      </div>
      
      {/* Animation Toggle */}
      <div className="flex items-center justify-center space-x-2 pt-4 mt-6">
        <Switch
          id="animation-toggle"
          checked={animationsEnabled}
          onCheckedChange={handleAnimationToggle}
          aria-label="Toggle background animations"
        />
        <Label htmlFor="animation-toggle" className="cursor-pointer">
          Enable Background Animations
        </Label>
      </div>
    </div>
  );
} 